/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import PageSkeleton from '../../components/common/page-skeleton';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import { partnersService } from '../../services/partners.service';
import { projectsService } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useValueSetOptions } from '../../hooks/useValueSetOptions';
import ScreenHeader from '../../components/common/screen-header';

const PARTNER_TYPE_OPTIONS = [
  { label: 'Government', value: 'GOVERNMENT' },
  { label: 'Vendor', value: 'VENDOR' },
  { label: 'Third party', value: 'THIRD_PARTY' },
  { label: 'Client', value: 'CLIENT' },
  { label: 'Internal', value: 'INTERNAL' },
];

const PARTNER_CATEGORY_OPTIONS = [
  { label: 'Internal (integration / credentials)', value: 'INTERNAL' },
  { label: 'Customer (owns projects)', value: 'CUSTOMER' },
];

// Mirrors the backend PartnerMaster create/update schemas. Type is required only
// for INTERNAL partners; CUSTOMER partners are global and carry no type/project.
const partnerSchema = z
  .object({
    partnerCategory: z.enum(['INTERNAL', 'CUSTOMER']),
    name: z.string().trim().min(1, 'Name is required').max(200, 'Name is too long'),
    partnerType: z.string().optional().or(z.literal('')),
    appId: z.string().optional().or(z.literal('')),
    contactPerson: z.string().max(200).optional().or(z.literal('')),
    contactPhoneNumber: z.string().max(50).optional().or(z.literal('')),
    contactPersonRole: z.string().max(200).optional().or(z.literal('')),
    website: z.string().max(500).optional().or(z.literal('')),
    address: z.string().max(500).optional().or(z.literal('')),
  })
  .refine((d) => d.partnerCategory === 'CUSTOMER' || !!d.partnerType, {
    message: 'Type is required',
    path: ['partnerType'],
  });

type PartnerFormData = z.infer<typeof partnerSchema>;

const EMPTY: PartnerFormData = {
  partnerCategory: 'INTERNAL',
  name: '',
  partnerType: '',
  appId: '',
  contactPerson: '',
  contactPhoneNumber: '',
  contactPersonRole: '',
  website: '',
  address: '',
};

const PartnerForm = () => {
  const navigate = useAppNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  // The partner's project when the edit page loaded — used to detect a project
  // change on save, which is a rescope (moves the partner's connections too).
  const originalAppIdRef = useRef<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PartnerFormData>({
    resolver: zodResolver(partnerSchema),
    defaultValues: EMPTY,
    mode: 'onChange',
  });

  // Partner type — admin-editable via the Value Sets screen, falling back to the
  // built-in list until the `partnerType` set is seeded.
  const partnerTypeOptions = useValueSetOptions('partnerType', PARTNER_TYPE_OPTIONS);

  const category = watch('partnerCategory');
  const isCustomer = category === 'CUSTOMER';

  // Projects the caller may create/rescope a partner in (INTERNAL only).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = useMemo(
    () => accessibleProjects.map((p) => ({ value: p._id, label: p.appCode ? `${p.name} (${p.appCode})` : p.name })),
    [accessibleProjects]
  );

  // Load the partner in edit mode.
  const { data: partner, isLoading } = useQuery({
    queryKey: ['partners', id],
    queryFn: () => partnersService.getById(id!),
    enabled: isEditMode && !!id,
  });

  useBreadcrumbTitle(isEditMode ? partner?.name : null);

  useEffect(() => {
    if (partner && isEditMode) {
      originalAppIdRef.current = partner.appId || null;
      reset({
        partnerCategory: (partner.partnerCategory as any) || 'INTERNAL',
        name: partner.name || '',
        partnerType: partner.partnerType || '',
        appId: partner.appId || '',
        contactPerson: partner.contactPerson || '',
        contactPhoneNumber: partner.contactPhoneNumber || '',
        contactPersonRole: partner.contactPersonRole || '',
        website: partner.website || '',
        address: partner.address || '',
      });
    }
  }, [partner, isEditMode, reset]);

  // On create, default an INTERNAL partner to the caller's first accessible project.
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !isCustomer && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, isCustomer, selectedAppId, accessibleProjects, setValue]);

  const notify = (severity: 'success' | 'error', detail: string) =>
    toast.current?.show({ severity, summary: severity === 'error' ? 'Error' : 'Success', detail, life: severity === 'error' ? 10000 : 3000 });

  const mutation = useMutation({
    mutationFn: async (data: PartnerFormData) => {
      const payload = {
        partnerCategory: data.partnerCategory,
        name: data.name.trim(),
        // Type applies to both categories (optional for CUSTOMER, required for
        // INTERNAL); project still applies to INTERNAL only.
        partnerType: data.partnerType || undefined,
        contactPerson: data.contactPerson || undefined,
        contactPhoneNumber: data.contactPhoneNumber || undefined,
        contactPersonRole: data.contactPersonRole || undefined,
        website: data.website || undefined,
        address: data.address || undefined,
      };
      const chosenAppId = isCustomer ? null : (data.appId || null);
      if (isEditMode && id) {
        await partnersService.update(id, payload as any);
        // A project change on edit is a rescope (the partner's connections move
        // with it) — a separate endpoint, only for INTERNAL partners.
        if (!isCustomer && chosenAppId && chosenAppId !== originalAppIdRef.current) {
          await partnersService.rescopeProject(id, chosenAppId);
        }
      } else {
        await partnersService.create(payload as any, chosenAppId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      notify('success', `Partner ${isEditMode ? 'updated' : 'created'} successfully.`);
      setTimeout(() => navigate('/partners'), 700);
    },
    onError: (err: any) => notify('error', extractErrorMessage(err, `Failed to ${isEditMode ? 'update' : 'create'} partner`)),
  });

  const onSubmit = (data: PartnerFormData) => mutation.mutate(data);
  const saving = mutation.isPending;

  if (isEditMode && isLoading) return <PageSkeleton />;

  return (
    <PermissionGuard jobName={JOB_NAMES.PARTNERS}>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-Partner-Form">
        {/* Header */}
        <ScreenHeader icon="pi pi-handshake" title={isEditMode ? 'Edit Partner' : 'Create New Partner'} subtitle={isEditMode ? 'Update partner information' : 'Add a new external partner or customer'} onBack={() => navigate('/partners')} />

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="ds-card p-4">
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-line">
              <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
                <i className="pi pi-info-circle text-blue-600" style={{ fontSize: '1rem' }} />
                Partner Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="partnerCategory"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect
                    {...field}
                    value={value || 'INTERNAL'}
                    onChange={(e) => {
                      onChange(e.target.value);
                      // CUSTOMER is global (no project) — clear appId on switch so a
                      // stale project can't linger. Type is kept: it's now allowed
                      // (optional) for CUSTOMER too.
                      if (e.target.value === 'CUSTOMER') {
                        setValue('appId', '');
                      }
                    }}
                    label="Category"
                    required
                    options={PARTNER_CATEGORY_OPTIONS}
                    helperText={
                      isCustomer
                        ? 'A company that owns projects (global; no data source of its own).'
                        : 'An integration/credentials partner that lives inside a project.'
                    }
                  />
                )}
              />

              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    label="Name"
                    required
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    autoFocus
                  />
                )}
              />

              <Controller
                name="partnerType"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Type"
                    required={!isCustomer}
                    options={partnerTypeOptions}
                    placeholder="Select type"
                    error={!!errors.partnerType}
                    helperText={errors.partnerType?.message}
                  />
                )}
              />


              {/* Project — INTERNAL partners only (CUSTOMER is global). On edit,
                  changing it rescopes the partner (its connections move too). */}
              {!isCustomer && (
                <Controller
                  name="appId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Project"
                      options={projectOptions}
                      placeholder="Select project"
                      helperText={isEditMode ? 'Changing this moves the partner and its connections to another project.' : undefined}
                    />
                  )}
                />
              )}

              <Controller
                name="contactPerson"
                control={control}
                render={({ field }) => (
                  <FormInput {...field} value={field.value || ''} label="Contact Person" />
                )}
              />

              <Controller
                name="contactPhoneNumber"
                control={control}
                render={({ field }) => (
                  <FormInput {...field} value={field.value || ''} label="Contact Phone" />
                )}
              />

              <Controller
                name="contactPersonRole"
                control={control}
                render={({ field }) => (
                  <FormInput {...field} value={field.value || ''} label="Contact Role" />
                )}
              />

              <Controller
                name="website"
                control={control}
                render={({ field }) => (
                  <FormInput {...field} value={field.value || ''} label="Website" />
                )}
              />

              <div className="md:col-span-2">
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <FormInput {...field} value={field.value || ''} label="Address" multiline rows={2} />
                  )}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 px-4 py-3 -mx-4 -mb-4 bg-canvas dark:bg-gray-900/50 border-t border-line flex justify-end gap-2 rounded-b-lg">
              <Button label="Cancel" severity="secondary" outlined onClick={() => navigate('/partners')} disabled={saving} size="small" />
              <Button
                type="submit"
                label={saving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
                icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                disabled={saving}
                size="small"
                testId="PartnerForm.Button.Save"
              />
            </div>
          </div>
        </form>
      </div>
    </PermissionGuard>
  );
};

export default PartnerForm;
